import React, { useState, useEffect } from "react";
import {
  Search,
  Calendar,
  Clock,
  Trash2,
  MessageSquare,
  Send,
  AlertCircle,
  CheckCircle,
  Plus,
  X,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { API_BASE_URL } from "../../config";
import Layout from "../Layout/Layout";
import Loader from "../utils/Loader";
import ReactPaginate from "react-paginate";
import { formatDateWithTime } from "../utils/formatDate";
import TrimText from "../utils/TrimText";
import WhatsAppLivePreview from "../shared/WhatsAppLivePreview.jsx";

const ScheduleMessagePage = () => {
  const { theme } = useTheme();
  const [receiverMobileNo, setReceiverMobileNo] = useState("");
  const [message, setMessage] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const messagesPerPage = 100;

  const showNotification = (message, type = "success") => {
    const id = Date.now();
    const notification = { id, message, type };
    setNotifications((prev) => [...prev, notification]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4000);
  };

  const fetchScheduledMessages = async () => {
    setLoading(true);
    const token = window.__appSession.getToken();
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/scheduled-messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      setScheduledMessages(data);
    } catch (error) {
      console.error("Error fetching scheduled messages:", error);
      showNotification("Failed to fetch scheduled messages.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduledMessages();
  }, []);

  const handleScheduleMessage = async (e) => {
    e.preventDefault();
    const timeToSend = new Date(scheduledTime).getTime();
    const currentTime = Date.now();

    if (timeToSend <= currentTime) {
      showNotification("Scheduled time must be in the future.", "error");
      return;
    }

    if (!receiverMobileNo || !message || !scheduledTime) {
      showNotification("Please fill in all fields.", "error");
      return;
    }

    const token = window.__appSession.getToken();
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/schedule-message`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ receiverMobileNo, message, scheduledTime }),
        },
      );

      const data = await response.json();
      showNotification(
        data.message || "Message scheduled successfully!",
        "success",
      );
      setReceiverMobileNo("");
      setMessage("");
      setScheduledTime("");
      setIsModalOpen(false);
      fetchScheduledMessages();
    } catch (error) {
      console.error("Error scheduling message:", error);
      showNotification("Failed to schedule message.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMessage = async (id) => {
    const token = window.__appSession.getToken();
    try {
      await fetch(`${API_BASE_URL}/api/whatsapp/scheduled-messages/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotification("Message deleted successfully.", "success");
      fetchScheduledMessages();
    } catch (error) {
      console.error("Error deleting message:", error);
      showNotification("Failed to delete message.", "error");
    }
  };

  const handleDeleteAllMessages = async () => {
    const token = window.__appSession.getToken();
    try {
      await fetch(
        `${API_BASE_URL}/api/whatsapp/scheduled-messages/delete/deleteall`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      showNotification("All messages deleted successfully.", "success");
      fetchScheduledMessages();
    } catch (error) {
      console.error("Error deleting all messages:", error);
      showNotification("Failed to delete all messages.", "error");
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0); // Reset to first page on search
  };

  const filteredMessages = scheduledMessages.filter((msg) => {
    const search = searchTerm.toLowerCase();
    return (
      (msg.receiverMobileNo &&
        msg.receiverMobileNo.toLowerCase().includes(search)) ||
      (msg.message && msg.message.toLowerCase().includes(search)) ||
      (msg.scheduledTime &&
        formatDateWithTime(msg.scheduledTime).toLowerCase().includes(search)) ||
      (typeof msg.sent === "boolean" &&
        (msg.sent ? "sent" : "pending").includes(search)) ||
      (msg._id && msg._id.toLowerCase().includes(search))
    );
  });

  const pendingCount = scheduledMessages.filter((msg) => !msg.sent).length;
  const sentCount = scheduledMessages.filter((msg) => msg.sent).length;

  // Pagination logic
  const pageCount = Math.ceil(filteredMessages.length / messagesPerPage);
  const offset = currentPage * messagesPerPage;
  const currentMessages = filteredMessages.slice(
    offset,
    offset + messagesPerPage,
  );

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  return (
    <>
      <Layout>
        <div>
          {/* Notifications */}
          <div className="fixed top-4 right-4 z-50 space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`rounded-xl border px-3 py-2 text-xs font-medium shadow-lg backdrop-blur ${
                  notification.type === "success"
                    ? "border-emerald-200 bg-emerald-500/95 text-white"
                    : notification.type === "error"
                      ? "border-rose-200 bg-rose-500/95 text-white"
                      : "border-indigo-200 bg-indigo-500/95 text-white"
                }`}
              >
                {notification.message}
              </div>
            ))}
          </div>

          {loading && <Loader theme={theme} text="Loading..." />}

          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-3 mb-3">
              {/* Header */}
              <div
                className={`glass-surface-strong ${theme.contentBg} ${theme.contentBorder} border rounded-2xl px-3 py-2`}
              >
                <div className="flex items-center gap-4 justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 p-2 shadow-[0_14px_30px_-22px_rgba(79,70,229,0.9)]">
                      <Calendar className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h1 className="text-base font-semibold text-slate-900">
                        Schedule Messages
                      </h1>
                      <p className="text-xs text-slate-500">
                        Manage your scheduled WhatsApp messages
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center space-x-3">
                    <div className="text-center">
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="h-3 w-3 text-emerald-500" />
                        <span className="text-xs font-medium text-slate-900">
                          {sentCount}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">Sent</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3 text-amber-500" />
                        <span className="text-xs font-medium text-slate-900">
                          {pendingCount}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">Pending</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div
                className={`glass-surface ${theme.contentBg} ${theme.contentBorder} border rounded-2xl flex items-center w-full px-3 py-2`}
              >
                <div className="flex flex-col sm:flex-row gap-3 w-full items-start sm:items-center justify-between">
                  {/* Search */}
                  <div className="relative flex-1 w-full sm:max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search messages..."
                      className={`w-full rounded-xl border border-indigo-200 bg-white/85 pl-9 pr-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="flex items-center space-x-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2 text-xs font-medium text-white transition hover:opacity-95"
                    >
                      <Plus className="h-3 w-3" />
                      <span>New Message</span>
                    </button>

                    <button
                      onClick={handleDeleteAllMessages}
                      disabled={scheduledMessages.length === 0}
                      className="flex items-center space-x-2 rounded-xl border border-rose-200 bg-rose-500 px-3 py-2 text-xs font-medium text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-3 w-3" />
                      <span>Delete All ({scheduledMessages.length})</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages Table */}
            <div
              className={`glass-surface ${theme.contentBg} ${theme.contentBorder} border rounded-2xl overflow-hidden`}
            >
              <div className="p-3 border-b border-indigo-200">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Scheduled Messages ({filteredMessages.length})
                </h3>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-indigo-100/90 via-violet-100/90 to-indigo-100/90 top-0 sticky z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        #
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Scheduled Time
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Mobile Number
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Message
                      </th>

                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Status
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-100/80">
                    {currentMessages.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center">
                          <AlertCircle className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-xs text-slate-500">
                            No scheduled messages found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      currentMessages.map((msg, idx) => (
                        <tr
                          key={msg._id}
                          className="transition-colors hover:bg-indigo-50/55"
                        >
                          <td className="px-3 py-1 text-xs text-slate-700 max-w-xs">
                            {offset + idx + 1}
                          </td>
                          <td className="px-3 py-1 text-xs text-slate-700">
                            {formatDateWithTime(msg.scheduledTime)}
                          </td>
                          <td className="px-3 py-1 text-xs font-medium text-slate-900">
                            {msg.receiverMobileNo}
                          </td>
                          <td className="px-3 py-1 text-xs text-slate-700 max-w-xs">
                            <div className="" title={msg.message}>
                              {<TrimText text={msg.message} maxLen={60} />}
                            </div>
                          </td>

                          <td className="px-3 py-1">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                msg.sent
                                  ? "border border-emerald-200 bg-emerald-100 text-emerald-700"
                                  : "border border-amber-200 bg-amber-100 text-amber-700"
                              }`}
                            >
                              {msg.sent ? (
                                <>
                                  <CheckCircle className="h-2 w-2 mr-1" />
                                  Sent
                                </>
                              ) : (
                                <>
                                  <Clock className="h-2 w-2 mr-1" />
                                  Pending
                                </>
                              )}
                            </span>
                          </td>
                          <td className="px-3 py-1">
                            <button
                              onClick={() => handleDeleteMessage(msg._id)}
                              className="rounded-md p-1 text-rose-500 transition-colors hover:bg-rose-50 hover:text-rose-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}

              {/* Table Footer */}
              <div className="border-t border-indigo-200 bg-indigo-50/40 px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-700">
                    Showing {currentMessages.length} of{" "}
                    {filteredMessages.length} messages
                  </div>
                  <div className="text-xs text-slate-500">
                    Total: {scheduledMessages.length} â€¢ Sent: {sentCount} â€¢
                    Pending: {pendingCount}
                  </div>
                  {pageCount > 1 && (
                    <div>
                      <ReactPaginate
                        previousLabel={"Prev"}
                        nextLabel={"Next"}
                        breakLabel={"..."}
                        pageCount={pageCount}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={5}
                        onPageChange={handlePageChange}
                        containerClassName={
                          "flex items-center justify-center space-x-2 text-xs"
                        }
                        pageClassName={"rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"}
                        pageLinkClassName={"text-slate-700"}
                        previousClassName={
                          "rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"
                        }
                        previousLinkClassName={"text-slate-700"}
                        nextClassName={"rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"}
                        nextLinkClassName={"text-slate-700"}
                        breakClassName={"px-3 py-1"}
                        breakLinkClassName={"text-slate-700"}
                        activeClassName={"!border-indigo-600 !bg-indigo-600 text-white"}
                        disabledClassName={"opacity-50 cursor-not-allowed"}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          {loading && <Loader theme={theme} text="Scheduling..." />}

          <div
            className={`glass-surface-strong ${theme.contentBg} w-full max-w-5xl rounded-2xl border border-indigo-200/70 shadow-xl`}
          >
            <div className="flex items-center justify-between border-b border-indigo-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center">
                <Send className="h-4 w-4 mr-2" />
                Schedule New Message
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-lg border border-indigo-200 p-1 text-slate-500 transition-colors hover:bg-indigo-50 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-4 p-4">
              <form onSubmit={handleScheduleMessage} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={receiverMobileNo}
                    onChange={(e) => setReceiverMobileNo(e.target.value)}
                    placeholder="+1234567890"
                    className={`w-full rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Scheduled Time
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className={`w-full rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Message
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message here..."
                    rows={3}
                    className={`w-full resize-none rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                    required
                  />
                </div>

                <div className="flex space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowMobilePreview(true)}
                    className="md:hidden flex-1 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-indigo-100"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 rounded-xl border border-indigo-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-indigo-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex flex-1 items-center justify-center space-x-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-xs font-medium text-white transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Calendar className="h-3 w-3" />
                    <span>Schedule</span>
                  </button>
                </div>
              </form>

              <div className="hidden md:block">
                <WhatsAppLivePreview
                  title="Scheduled Preview"
                  receiverMobileNo={receiverMobileNo}
                  message={message}
                  scheduleTime={scheduledTime}
                />
              </div>
            </div>

            {showMobilePreview && (
              <div className="fixed inset-0 z-50 md:hidden">
                <div
                  className="absolute inset-0 bg-black/45"
                  onClick={() => setShowMobilePreview(false)}
                />
                <div className="absolute bottom-0 left-0 right-0 max-h-[78vh] overflow-y-auto rounded-t-2xl border border-indigo-200 bg-white/95 p-3 shadow-2xl backdrop-blur">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-800">Live Preview</h4>
                    <button
                      type="button"
                      onClick={() => setShowMobilePreview(false)}
                      className="rounded border border-indigo-200 px-2 py-1 text-xs text-slate-700"
                    >
                      Close
                    </button>
                  </div>
                  <WhatsAppLivePreview
                    title="Scheduled Preview"
                    receiverMobileNo={receiverMobileNo}
                    message={message}
                    scheduleTime={scheduledTime}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleMessagePage;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { API_BASE_URL } from "../../config";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import "./style.css";
// import { motion } from "framer-motion"; // For animations
// import Layout from "../Layout/Layout"; // Import the enhanced Layout component

// const Footer = () => {
//   return (
//     <footer className="bg-gradient-to-r from-green-600 to-teal-500 text-white py-4 text-center shadow-inner">
//       <p className="text-sm tracking-wide">
//         Â© {new Date().getFullYear()} Smart ItBox. All rights reserved.
//       </p>
//     </footer>
//   );
// };

// const ScheduleMessagePage = () => {
//   const [receiverMobileNo, setReceiverMobileNo] = useState("");
//   const [message, setMessage] = useState("");
//   const [scheduledTime, setScheduledTime] = useState("");
//   const [scheduledMessages, setScheduledMessages] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [loading, setLoading] = useState(false);

//   const fetchScheduledMessages = async () => {
//     setLoading(true);
//     const token = window.__appSession.getToken();
//     try {
//       const response = await axios.get(
//         `${API_BASE_URL}/api/whatsapp/scheduled-messages`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setScheduledMessages(response.data);
//     } catch (error) {
//       console.error("Error fetching scheduled messages:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchScheduledMessages();
//   }, []);

//   const handleScheduleMessage = async (e) => {
//     e.preventDefault();
//     const timeToSend = new Date(scheduledTime).getTime();
//     const currentTime = Date.now();

//     if (timeToSend <= currentTime) {
//       toast.error("Scheduled time must be in the future.");
//       return;
//     }

//     const token = window.__appSession.getToken();
//     try {
//       const response = await axios.post(
//         `${API_BASE_URL}/api/whatsapp/schedule-message`,
//         { receiverMobileNo, message, scheduledTime },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );
//       toast.success(response.data.message);
//       setReceiverMobileNo("");
//       setMessage("");
//       setScheduledTime("");
//       fetchScheduledMessages();
//     } catch (error) {
//       console.error("Error scheduling message:", error);
//       toast.error("Failed to schedule message.");
//     }
//   };

//   const handleDeleteMessage = async (id) => {
//     const token = window.__appSession.getToken();
//     try {
//       await axios.delete(
//         `${API_BASE_URL}/api/whatsapp/scheduled-messages/${id}`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       toast.success("Message deleted successfully.");
//       fetchScheduledMessages();
//     } catch (error) {
//       console.error("Error deleting message:", error);
//       toast.error("Failed to delete message.");
//     }
//   };

//   const handleDeleteAllMessages = async () => {
//     const token = window.__appSession.getToken();
//     try {
//       await axios.delete(
//         `${API_BASE_URL}/api/whatsapp/scheduled-messages/delete/deleteall`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       toast.success("All messages deleted successfully.");
//       fetchScheduledMessages();
//     } catch (error) {
//       console.error("Error deleting all messages:", error);
//       toast.error("Failed to delete all messages.");
//     }
//   };

//   const handleSearchChange = (e) => {
//     setSearchTerm(e.target.value);
//   };

//   const filteredMessages = scheduledMessages.filter((msg) =>
//     msg.receiverMobileNo.includes(searchTerm)
//   );

//   return (
//     <Layout>
//       <motion.main
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ duration: 0.8 }}
//         className="flex-grow w-full min-h-[calc(100vh-4rem)] py-6 px-4 sm:px-6"
//       >
//         {loading && (
//           <motion.div
//             className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//           >
//             <svg
//               className="animate-spin h-12 w-12 text-[#25D366]"
//               xmlns="http://www.w3.org/2000/svg"
//               viewBox="0 0 24 24"
//             >
//               <circle
//                 className="opacity-25"
//                 cx="12"
//                 cy="12"
//                 r="10"
//                 stroke="currentColor"
//                 strokeWidth="4"
//               ></circle>
//               <path
//                 className="opacity-75"
//                 fill="currentColor"
//                 d="M4 12a8 8 0 1 1 16 0A8 8 0 0 1 4 12z"
//               ></path>
//             </svg>
//           </motion.div>
//         )}

//         <div className="bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-6 sm:p-8 w-full max-w-5xl mx-auto border border-gray-200 transition-all">
//           <h1 className="text-3xl sm:text-4xl font-bold text-center mb-6 text-[#25D366] tracking-tight font-serif">
//             Schedule WhatsApp Message
//           </h1>

//           {/* Search Bar */}
//           <motion.div whileFocus={{ scale: 1.02 }} className="mb-6 relative">
//             <input
//               type="text"
//               value={searchTerm}
//               onChange={handleSearchChange}
//               placeholder="Search by phone number..."
//               className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//             />
//             <svg
//               className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500"
//               xmlns="http://www.w3.org/2000/svg"
//               fill="none"
//               viewBox="0 0 24 24"
//               stroke="currentColor"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth="2"
//                 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
//               />
//             </svg>
//           </motion.div>

//           {/* Display Total Count */}
//           <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-gray-800 tracking-wide">
//             Total Scheduled Messages: {scheduledMessages.length}
//           </h2>

//           {/* Button to Delete All Messages */}
//           <motion.button
//             whileHover={{
//               scale: 1.05,
//               boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
//             }}
//             whileTap={{ scale: 0.95 }}
//             onClick={handleDeleteAllMessages}
//             className="mb-6 bg-red-500 text-white py-2 px-4 rounded-lg shadow-md hover:bg-red-600 transition-all"
//           >
//             Delete All Messages
//           </motion.button>

//           {/* Display Scheduled Messages */}
//           <h2 className="text-xl sm:text-2xl font-semibold mt-6 text-gray-800 tracking-wide">
//             Scheduled Messages
//           </h2>
//           <div className="mt-4 space-y-4">
//             {filteredMessages.length === 0 ? (
//               <p className="text-gray-600 text-center">
//                 No scheduled messages found.
//               </p>
//             ) : (
//               filteredMessages.map((msg) => (
//                 <motion.div
//                   key={msg._id}
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ duration: 0.3 }}
//                   className="bg-white/50 backdrop-blur-sm border border-gray-200 p-4 rounded-lg shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center transition-all hover:scale-[1.01] hover:shadow-md"
//                 >
//                   <div className="space-y-1">
//                     <p className="text-gray-800">
//                       <span className="font-medium">Receiver:</span>{" "}
//                       {msg.receiverMobileNo}
//                     </p>
//                     <p className="text-gray-800">
//                       <span className="font-medium">Message:</span>{" "}
//                       {msg.message}
//                     </p>
//                     <p className="text-gray-800">
//                       <span className="font-medium">Scheduled Time:</span>{" "}
//                       {new Date(msg.scheduledTime).toLocaleString()}
//                     </p>
//                     <p className="mt-2">
//                       <span
//                         className={`inline-block px-2 py-1 rounded-full text-sm ${
//                           msg.sent
//                             ? "bg-green-200 text-green-800"
//                             : "bg-yellow-200 text-yellow-800"
//                         }`}
//                       >
//                         Status: {msg.sent ? "Sent" : "Pending"}
//                       </span>
//                     </p>
//                   </div>
//                   <motion.button
//                     whileHover={{
//                       scale: 1.05,
//                       boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
//                     }}
//                     whileTap={{ scale: 0.95 }}
//                     onClick={() => handleDeleteMessage(msg._id)}
//                     className="mt-3 sm:mt-0 bg-red-500 text-white py-1 px-3 rounded-lg shadow-md hover:bg-red-600 transition-all"
//                   >
//                     Delete
//                   </motion.button>
//                 </motion.div>
//               ))
//             )}
//           </div>

//           <ToastContainer />
//         </div>
//       </motion.main>
//     </Layout>
//   );
// };

// export default ScheduleMessagePage;
